import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Auth, User as FirebaseUser } from 'firebase/auth';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { notMemberMessage, type UserRole } from '@rhwb/types';

export interface AuthUser {
  email: string;
  role: UserRole;
  name?: string;
  id: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  /** Legacy compat: { access_token } when signed in, null otherwise */
  session: { access_token: string } | null;
  // source: 'survey' marks a sign-in started from the survey link, so the API
  // can count it in the survey funnel.
  login: (email: string, rememberMe?: boolean, options?: { source?: 'survey' }) => Promise<{ success: boolean; error?: string; fromSession?: boolean }>;
  loginWithOtp: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isEmailSent: boolean;
  clearEmailSent: () => void;
  authError: string | null;
}

/** Kept for backwards compat — no longer used internally */
export interface AuthAuditLog {
  email_id: string;
  event_name: string;
  value_text: string | null;
  value_label?: string;
}

export interface AuthConfig {
  firebaseAuth: Auth;
  /** Cloud Run base URL, e.g. "https://qual-scores-api-xxx.a.run.app" */
  cloudRunUrl: string;
  appName: string;
  appDisplayName?: string;
  /**
   * Called just before Firebase signOut, for teardown that needs a live
   * credential or must not outlive the session (usage tracking, caches).
   * A throw here is swallowed — nothing may block sign-out.
   */
  onBeforeLogout?: () => void;
  /**
   * Sign in RHWB members only (runners_profile, a coach/admin/lord role, or a
   * store-review account). The API refuses the sign-in email to anyone else,
   * and anyone already signed in who isn't a member is signed out, with a
   * message asking for their registered email.
   */
  requireMembership?: boolean;
}

export function createAuthContext(config: AuthConfig): {
  AuthProvider: React.FC<{ children: ReactNode }>;
  useAuth: () => AuthContextType;
} {
  const { firebaseAuth, cloudRunUrl, appName, onBeforeLogout, requireMembership } = config;
  const EMAIL_KEY = `${appName}-emailForSignIn`;

  const AuthContext = createContext<AuthContextType | undefined>(undefined);

  const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [idToken, setIdToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const fetchRole = async (fbUser: FirebaseUser): Promise<AuthUser | null> => {
      try {
        const token = await fbUser.getIdToken();
        setIdToken(token);
        const url = cloudRunUrl ? `${cloudRunUrl}/api/whoami` : '/api/whoami';
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('[AUTH] /api/whoami returned', res.status);
          return null;
        }
        const data = await res.json() as { email: string; role: string; name: string | null; member?: boolean };
        // Signed in, but with an address that isn't a member's — usually a
        // second email. An older API omits `member`; treat that as a member.
        if (requireMembership && data.member === false) {
          try { onBeforeLogout?.(); } catch { /* never block sign-out */ }
          await signOut(firebaseAuth);
          localStorage.removeItem(EMAIL_KEY);
          setIsEmailSent(false);
          setAuthError(notMemberMessage(data.email));
          return null;
        }
        return {
          email: data.email,
          role: data.role as UserRole,
          name: data.name ?? undefined,
          id: fbUser.uid,
        };
      } catch (err) {
        console.error('[AUTH] fetchRole error:', err);
        return null;
      }
    };

    useEffect(() => {
      const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
        if (fbUser) {
          setFirebaseUser(fbUser);
          const authUser = await fetchRole(fbUser);
          setUser(authUser);
        } else {
          setFirebaseUser(null);
          setIdToken(null);
          setUser(null);
        }
        setIsLoading(false);
      });
      return () => unsubscribe();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async (
      email: string,
      rememberMe: boolean = true,
      options: { source?: 'survey' } = {}
    ): Promise<{ success: boolean; error?: string; fromSession?: boolean }> => {
      try {
        setAuthError(null);

        const current = firebaseAuth.currentUser;
        if (current && current.email?.toLowerCase() === email.toLowerCase()) {
          return { success: true, fromSession: true };
        }

        await setPersistence(
          firebaseAuth,
          rememberMe ? browserLocalPersistence : browserSessionPersistence
        );

        const callbackUrl = `${window.location.origin}/auth/callback`;

        // Server generates the Firebase magic link + OTP and sends one combined email
        if (cloudRunUrl) {
          const res = await fetch(`${cloudRunUrl}/send-auth-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              callbackUrl,
              ...(requireMembership ? { requireMember: true } : {}),
              ...(options.source ? { source: options.source } : {}),
            }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({})) as { error?: string };
            throw new Error(data.error || 'Failed to send sign-in email');
          }
        }
        localStorage.setItem(EMAIL_KEY, email);
        setIsEmailSent(true);
        return { success: true };
      } catch (err: any) {
        console.error('[AUTH] login error:', err);
        const message: string = err?.message || 'Failed to send sign-in link';
        setAuthError(message);
        return { success: false, error: message };
      }
    };

    const logout = async () => {
      // Let the app end its usage session before the credential goes: a beat
      // fired after signOut has no token and would be dropped anyway, and a
      // stored session id must not survive into the next sign-in.
      try { onBeforeLogout?.(); } catch { /* never block sign-out */ }
      await signOut(firebaseAuth);
      setUser(null);
      setFirebaseUser(null);
      setIdToken(null);
      setIsEmailSent(false);
      setAuthError(null);
      localStorage.removeItem(EMAIL_KEY);
    };

    const loginWithOtp = async (
      email: string,
      code: string
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const url = cloudRunUrl ? `${cloudRunUrl}/verify-auth-code` : '/verify-auth-code';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim() }),
        });
        const data = await res.json().catch(() => ({})) as { token?: string; error?: string };
        if (!res.ok) {
          return { success: false, error: data.error || 'Invalid or expired code' };
        }
        await signInWithCustomToken(firebaseAuth, data.token!);
        return { success: true };
      } catch {
        return { success: false, error: 'Verification failed. Please try again.' };
      }
    };

    const clearEmailSent = () => setIsEmailSent(false);

    const session = idToken ? { access_token: idToken } : null;

    return (
      <AuthContext.Provider
        value={{
          user,
          firebaseUser,
          session,
          login,
          loginWithOtp,
          logout,
          isAuthenticated: !!user,
          isLoading,
          isEmailSent,
          clearEmailSent,
          authError,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  };

  const useAuth = (): AuthContextType => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
  };

  return { AuthProvider, useAuth };
}
